import { useState } from "react";
import dayjs from "dayjs";

import Col from "react-bootstrap/Col";
import Row from "react-bootstrap/Row";
import Modal from "react-bootstrap/Modal";
import Card from "react-bootstrap/Card";
import { FaDatabase } from "react-icons/fa";

import { Paginated } from "@/@types/generics/paginated";
import { getQuarterDates } from "@/utils/quarters";
import { exportExcel } from "@/utils/exports";
import { GenericObject } from "@/@types/generics/genericals";
import { getAge } from "@/utils/dateUtil";
import { DetailedCitizenVaccinationsIndicatorC3Data } from "@/services/esus/indicator/esf/c3/getDetailedCitizensVaccinationsIndicatorC3";

import { IndicatorEsfC3CitizensVaccinationsFilters } from "./IndicatorEsfC3CitizensVaccinationsFilters";
import {
	getCitizenVaccinationStatusDescription,
	IndicatorEsfC3CitizensVaccinationsTable,
} from "./IndicatorEsfC3CitizensVaccinationsTable";
import { IndicatorEsfC3CitizensVaccinationsHistory } from "./IndicatorEsfC3CitizensVaccinationsHistory";
import { PaginationLinks } from "@/components/PaginationLinks";
import { NoRecordsFeedback } from "@/components/NoRecordsFeedback";
import { LoadingScreen } from "@/components/LoadingScreen";

type IndicatorEsfC3CitizensVaccinationsProps = {
	selectedQuarter: string;
	search: string;
	setSearch: (search: string) => void;
	isEvaluated: boolean | undefined;
	setIsEvaluated: (isEvaluated: boolean | undefined) => void;
	isLoading: boolean;
	paginatedCitizensVaccinations: Paginated<DetailedCitizenVaccinationsIndicatorC3Data>;
	pageNumber: number;
	setPageNumber: (pageNumber: number) => void;
	handleGetAllDetailedCitizensVaccinationsIndicatorC3: () => Promise<
		DetailedCitizenVaccinationsIndicatorC3Data[]
	>;
};

export function IndicatorEsfC3CitizensVaccinations({
	selectedQuarter,
	search,
	setSearch,
	isEvaluated,
	setIsEvaluated,
	isLoading,
	paginatedCitizensVaccinations,
	pageNumber,
	setPageNumber,
	handleGetAllDetailedCitizensVaccinationsIndicatorC3,
}: Readonly<IndicatorEsfC3CitizensVaccinationsProps>) {
	const [isCitizenHistoryModalOpen, setIsCitizenHistoryModalOpen] = useState(false);
	const [citizenWithVaccinations, setCitizenWithVaccinations] =
		useState<DetailedCitizenVaccinationsIndicatorC3Data>(
			new DetailedCitizenVaccinationsIndicatorC3Data()
		);
	const [isLoadingCitizenWithVaccinations, setIsLoadingCitizenWithVaccinations] = useState(false);
	const [isLoadingExportData, setIsLoadingExportData] = useState(false);

	async function handleGetCitizenWithAttendances(
		citizenVaccinations: DetailedCitizenVaccinationsIndicatorC3Data
	) {
		setIsLoadingCitizenWithVaccinations(true);
		setCitizenWithVaccinations(citizenVaccinations);
		setIsLoadingCitizenWithVaccinations(false);
		setIsCitizenHistoryModalOpen(true);
	}

	async function handleExportTable() {
		setIsLoadingExportData(true);
		const attendances = await handleGetAllDetailedCitizensVaccinationsIndicatorC3();
		setIsLoadingExportData(false);
		const quarterDates = getQuarterDates(selectedQuarter);

		if (Object.keys(attendances).length > 0) {
			const excelData: GenericObject[] =
				transformCitizensWithVaccinationsToGenericObjectArray(attendances);
			exportExcel(
				excelData,
				`Vacinações - ${dayjs(quarterDates.startDate).format("DD-MM-YYYY")} à ${dayjs(
					quarterDates.endDate
				).format("DD/MM/YYYY")}`
			);
		}
	}

	function transformCitizensWithVaccinationsToGenericObjectArray(
		citizensWithVaccinations: DetailedCitizenVaccinationsIndicatorC3Data[]
	) {
		return citizensWithVaccinations.map(
			(citizenWithVaccinations) =>
				({
					Nome: citizenWithVaccinations.fullName,
					DN: dayjs(citizenWithVaccinations.birthdate).format("DD/MM/YYYY"),
					Idade: getAge(citizenWithVaccinations.birthdate),
					Documento: citizenWithVaccinations.cpf ?? citizenWithVaccinations.cns ?? "",
					Equipe: citizenWithVaccinations.healthTeam?.name ?? "",
					Ine: citizenWithVaccinations.healthTeam?.ine ?? "",
					Microarea: citizenWithVaccinations.area ?? "FA",
					Dum: dayjs(citizenWithVaccinations.dumDate).format("DD/MM/YYYY"),
					Dpp: dayjs(citizenWithVaccinations.dppDate).format("DD/MM/YYYY"),
					IG: citizenWithVaccinations.gestationalAge ?? "",
					DTPa: citizenWithVaccinations.vaccines?.length ?? 0,
					Status: getCitizenVaccinationStatusDescription(
						citizenWithVaccinations.vaccinationStatus
					),
				} as GenericObject)
		);
	}

	return (
		<Row className="bg-white m-3 p-2">
			<Col>
				<LoadingScreen loading={isLoadingCitizenWithVaccinations || isLoadingExportData} />
				<Card>
					<Card.Header>
						<IndicatorEsfC3CitizensVaccinationsFilters
							search={search}
							setSearch={setSearch}
							isEvaluated={isEvaluated}
							setIsEvaluated={setIsEvaluated}
							handleExportTable={handleExportTable}
						/>
					</Card.Header>
					<Card.Body>
						<IndicatorEsfC3CitizensVaccinationsTable
							citizensVaccinations={paginatedCitizensVaccinations.data}
							isLoading={isLoading}
							openCitizenHistory={handleGetCitizenWithAttendances}
						/>
					</Card.Body>
					<Card.Footer>
						{paginatedCitizensVaccinations?.totalRecords ? (
							<PaginationLinks
								itemsPerPage={paginatedCitizensVaccinations.pageSize}
								totalPages={paginatedCitizensVaccinations.totalRecords}
								changeSelectedPage={(pageNumber) => {
									setPageNumber(pageNumber);
								}}
								pageNumber={pageNumber || 0}
							/>
						) : (
							!isLoading && (
								<NoRecordsFeedback
									message={"Nenhum resultado encontrado."}
									icon={<FaDatabase />}
								/>
							)
						)}
					</Card.Footer>
				</Card>
				<Modal
					show={isCitizenHistoryModalOpen}
					onHide={() => setIsCitizenHistoryModalOpen(false)}
					size="xl"
					aria-labelledby="contained-modal-title-vcenter"
					centered
				>
					<Modal.Header closeButton>
						<Modal.Title>
							<h3 className="text-uppercase text-dark fw-bold fs-7">
								Vacinas aplicadas em gestantes
							</h3>
						</Modal.Title>
					</Modal.Header>
					<Modal.Body>
						<IndicatorEsfC3CitizensVaccinationsHistory
							citizenWithVaccinations={citizenWithVaccinations}
						/>
					</Modal.Body>
				</Modal>
			</Col>
		</Row>
	);
}
